import type { CalendarDay } from '@lib/validators/temporal.validator'

/**
 * Formatting ad numbers (docs/ad-analytics.spec.md, "The numbers"). Days are an
 * ad account's own days — calendar days, formatted in UTC so none slips a day in
 * the reader's zone.
 */

/**
 * An amount in micros (millionths of the currency's unit, as Google reports
 * spend) as money in that currency: `$1,840.00`.
 */
export function formatMicros(micros: number, currencyCode: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(micros / 1_000_000)
}

export function formatCount(n: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n)
}

/** Conversions as Google shows them: whole when whole, otherwise to two decimals. */
export function formatConversions(n: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n)
}

/** A fraction as a percentage: 0.0234 → `2.34%`. */
export function formatRate(fraction: number): string {
  return new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 2 }).format(fraction)
}

function utc(day: CalendarDay): Date {
  return new Date(`${day}T00:00:00.000Z`)
}

/** `Oct 13`, or `Oct 13, 2025` when not this year. */
export function formatAdDay(day: CalendarDay, now: Date = new Date()): string {
  const date = utc(day)
  const year = date.getUTCFullYear() === now.getUTCFullYear() ? undefined : 'numeric'
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year, timeZone: 'UTC' })
}

/** A period's days: `Oct 11–24, 2026`, `Sep 27 – Oct 10, 2026`, `Dec 28, 2025 – Jan 10, 2026`. */
export function formatAdPeriod(start: CalendarDay, end: CalendarDay): string {
  const from = utc(start)
  const to = utc(end)
  const monthDay = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
  const year = (d: Date) => String(d.getUTCFullYear())
  if (from.getUTCFullYear() !== to.getUTCFullYear()) return `${monthDay(from)}, ${year(from)} – ${monthDay(to)}, ${year(to)}`
  if (from.getUTCMonth() === to.getUTCMonth()) return `${monthDay(from)}–${String(to.getUTCDate())}, ${year(to)}`
  return `${monthDay(from)} – ${monthDay(to)}, ${year(to)}`
}
